import React, { useEffect, useMemo, useState } from 'react'
import { useKPISettingList } from './hooks'
import { CCardBody } from '@coreui/react-pro'
import {
  Alert,
  Button,
  Divider,
  Flex,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { UserSettings } from './types'
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  StarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { putUserSetting, updateManagersQuantity } from './model'
import { DepositExclusions } from '../widgets/DepositExclusions'

const { Text } = Typography

export const KPISettings: React.FC = () => {
  const {
    kpiSettingList,
    loading,
    saving,
    savingQty,
    onChangeKPISettingsList,
    onSaveUserSetting,
    onUpdateManagersQuantity,
    totalCountOfKPI,
  } = useKPISettingList()

  const [periodValue, setPeriodValue] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Inline edit state for user settings
  const [editingKey, setEditingKey] = useState<number | null>(null)
  const [editingRow, setEditingRow] = useState<{
    evaluation: number
    isIgnoreSpeedCounting: boolean
  } | null>(null)

  // Inline edit state for managers quantity
  const [editingQty, setEditingQty] = useState(false)
  const [tempQty, setTempQty] = useState<number>(0)

  // Counter to trigger reload after successful saves
  const [reloadTrigger, setReloadTrigger] = useState(0)

  useEffect(() => {
    const unsubUser = putUserSetting.doneData.watch(() => {
      setEditingKey(null)
      setEditingRow(null)
      setReloadTrigger((c) => c + 1)
    })
    const unsubQty = updateManagersQuantity.doneData.watch(() => {
      setEditingQty(false)
      setReloadTrigger((c) => c + 1)
    })
    return () => {
      unsubUser()
      unsubQty()
    }
  }, [])

  useEffect(() => {
    onChangeKPISettingsList({ limit: pageSize, page })
  }, [pageSize, page, reloadTrigger])

  const periodOptions = useMemo(
    () =>
      kpiSettingList.map((item) => ({
        label: item.periodName,
        value: item.periodName,
      })),
    [kpiSettingList],
  )

  // Auto-select first period when list loads
  useEffect(() => {
    if (kpiSettingList.length > 0 && !periodValue) {
      setPeriodValue(kpiSettingList[0].periodName)
    }
  }, [kpiSettingList])

  const tableDataSource = useMemo(
    () => kpiSettingList.find((item) => item.periodName === periodValue),
    [periodValue, kpiSettingList],
  )

  const isCurrentMonth = useMemo(() => {
    if (!tableDataSource) return false
    const now = new Date()
    return (
      tableDataSource.year === now.getFullYear() &&
      tableDataSource.month === now.getMonth() + 1
    )
  }, [tableDataSource])

  const startEdit = (record: UserSettings) => {
    setEditingKey(record.id)
    setEditingRow({
      evaluation: record.evaluation,
      isIgnoreSpeedCounting: record.isIgnoreSpeedCounting,
    })
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setEditingRow(null)
  }

  const saveEdit = (id: number) => {
    if (!editingRow) return
    onSaveUserSetting({
      id,
      evaluation: editingRow.evaluation,
      isIgnoreSpeedCounting: editingRow.isIgnoreSpeedCounting,
    })
  }

  const columns: ColumnsType<UserSettings> = [
    {
      title: 'Manager',
      fixed: 'left',
      width: 180,
      sorter: (a, b) => a.user.username.localeCompare(b.user.username),
      defaultSortOrder: 'ascend',
      render: (_, record: UserSettings) => (
        <Text strong style={{ fontSize: 12 }}>
          {record.user.username}
        </Text>
      ),
    },
    {
      title: (
        <Tooltip title="Оценка руководителя. По умолчанию 100. Положительное число с точностью до 2 знаков после запятой.">
          <Space size={4}>
            <StarOutlined style={{ color: '#f59e0b' }} />
            <span>Assessment</span>
            <InfoCircleOutlined style={{ color: '#545c6e', fontSize: 11 }} />
          </Space>
        </Tooltip>
      ),
      dataIndex: 'evaluation',
      width: 200,
      sorter: (a, b) => a.evaluation - b.evaluation,
      render: (val: number, record: UserSettings) => {
        if (editingKey === record.id && editingRow) {
          return (
            <InputNumber
              autoFocus
              min={0}
              max={200}
              precision={2}
              value={editingRow.evaluation}
              onChange={(v) =>
                setEditingRow((r) =>
                  r ? { ...r, evaluation: v ?? 0 } : r,
                )
              }
              size="small"
              style={{ width: 90 }}
              controls={false}
            />
          )
        }
        return (
          <Flex align="center" gap={6}>
            <StarOutlined style={{ color: '#f59e0b', fontSize: 12 }} />
            <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
              {val}
            </span>
          </Flex>
        )
      },
    },
    {
      title: (
        <Tooltip title="Учитывать метрику скорости при расчёте KPI для данного менеджера. По умолчанию — да.">
          <Space size={4}>
            <ThunderboltOutlined style={{ color: '#fbbf24' }} />
            <span>Speed counting</span>
            <InfoCircleOutlined style={{ color: '#545c6e', fontSize: 11 }} />
          </Space>
        </Tooltip>
      ),
      dataIndex: 'isIgnoreSpeedCounting',
      width: 180,
      render: (val: boolean, record: UserSettings) => {
        if (editingKey === record.id && editingRow) {
          return (
            <Switch
              checked={editingRow.isIgnoreSpeedCounting}
              onChange={(v) =>
                setEditingRow((r) =>
                  r ? { ...r, isIgnoreSpeedCounting: v } : r,
                )
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
              size="small"
            />
          )
        }
        return val ? (
          <Tag
            color="success"
            icon={<ThunderboltOutlined />}
            style={{ fontSize: 11 }}
          >
            Yes
          </Tag>
        ) : (
          <Tag color="default" style={{ fontSize: 11 }}>
            No
          </Tag>
        )
      },
    },
    {
      title: 'Action',
      width: 140,
      align: 'center',
      render: (_, record: UserSettings) => {
        if (isCurrentMonth) {
          return (
            <Tag style={{ fontSize: 10 }}>Locked</Tag>
          )
        }
        if (editingKey === record.id) {
          return (
            <Space>
              <Tooltip title="Save">
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => saveEdit(record.id)}
                  loading={saving}
                  style={{ background: '#22c55e', borderColor: '#22c55e' }}
                />
              </Tooltip>
              <Tooltip title="Cancel">
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={cancelEdit}
                />
              </Tooltip>
            </Space>
          )
        }
        return (
          <Tooltip title="Edit this manager">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => startEdit(record)}
              disabled={editingKey !== null}
            >
              Edit
            </Button>
          </Tooltip>
        )
      },
    },
  ]

  const handleTableChange = ({
    currentPage,
    currentPageSize,
  }: {
    currentPage?: number
    currentPageSize?: number
  }) => {
    if (currentPage) setPage(currentPage)
    if (currentPageSize) setPageSize(currentPageSize)
  }

  return (
    <CCardBody>
      {/* Period selector + Managers quantity */}
      <Flex
        align="center"
        gap={24}
        wrap="wrap"
        className="mb-3"
        style={{ padding: '8px 0' }}
      >
        <Space>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Period
          </Text>
          <Select
            options={periodOptions}
            value={periodValue || undefined}
            style={{ width: 130 }}
            onChange={(value) => {
              setPeriodValue(value)
              cancelEdit()
            }}
          />
        </Space>

        {tableDataSource?.managersQuantity != null && (
          <>
            <Divider type="vertical" />
            <Space align="center" size={8}>
              <TeamOutlined style={{ color: '#3b82f6' }} />
              <Tooltip title="Количество менеджеров за период. Используется для расчёта справедливой доли в KPI.">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Managers in period:
                </Text>
              </Tooltip>

              {editingQty ? (
                <>
                  <InputNumber
                    autoFocus
                    min={0.01}
                    precision={2}
                    value={tempQty}
                    onChange={(v) => setTempQty(v ?? 0)}
                    size="small"
                    style={{ width: 90 }}
                    controls={false}
                  />
                  <Tooltip title="Save">
                    <Button
                      type="primary"
                      size="small"
                      icon={<CheckOutlined />}
                      loading={savingQty}
                      onClick={() => {
                        if (tableDataSource) {
                          onUpdateManagersQuantity({
                            id: tableDataSource.id,
                            managersQuantity: tempQty,
                          })
                        }
                      }}
                      style={{ background: '#22c55e', borderColor: '#22c55e' }}
                    />
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <Button
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setTempQty(tableDataSource?.managersQuantity ?? 0)
                        setEditingQty(false)
                      }}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <Text
                    strong
                    style={{ fontFamily: 'monospace', fontSize: 15 }}
                  >
                    {tableDataSource.managersQuantity}
                  </Text>
                  <Tooltip title="Edit managers quantity">
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setTempQty(tableDataSource.managersQuantity)
                        setEditingQty(true)
                      }}
                      style={{ color: '#3b82f6' }}
                    />
                  </Tooltip>
                </>
              )}
            </Space>
          </>
        )}

        {/* Status badges */}
        <div style={{ marginLeft: 'auto' }}>
          {isCurrentMonth && (
            <Tag color="warning" icon={<InfoCircleOutlined />}>
              Current month — manager settings locked
            </Tag>
          )}
          {!isCurrentMonth && tableDataSource && (
            <Tag color="blue" icon={<EditOutlined />}>
              Past period — all settings editable
            </Tag>
          )}
        </div>
      </Flex>

      {/* Current month alert */}
      {isCurrentMonth && (
        <Alert
          type="info"
          showIcon
          message="Manager settings are only available for completed months."
          description="You can update the managers quantity above. Switch to a past month to edit assessment and speed counting."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Editing alert */}
      {editingKey && (
        <Alert
          type="warning"
          showIcon
          message="You have unsaved changes. Save or cancel before editing another manager."
          style={{ marginBottom: 12 }}
        />
      )}

      {/* Table */}
      {tableDataSource?.userSettings ? (
        <Table
          dataSource={tableDataSource.userSettings}
          bordered
          size="small"
          scroll={{ x: 700 }}
          loading={loading}
          rowKey="id"
          columns={columns}
          pagination={{
            showSizeChanger: true,
            pageSize: pageSize,
            total: totalCountOfKPI,
          }}
          rowClassName={(r) => (r.id === editingKey ? 'editing-row' : '')}
          onChange={(pagination) =>
            handleTableChange({
              currentPage: pagination.current,
              currentPageSize: pagination.pageSize,
            })
          }
        />
      ) : null}

      {/* Legend */}
      <Flex gap={16} style={{ marginTop: 12 }} align="center" wrap="wrap">
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600 }}
        >
          Assessment:
        </Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          positive number, up to 2 decimal places, default 100
        </Text>
        <Divider type="vertical" />
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600 }}
        >
          Speed counting:
        </Text>
        <Tag
          color="success"
          icon={<ThunderboltOutlined />}
          style={{ fontSize: 10 }}
        >
          Yes — included in KPI
        </Tag>
        <Tag color="default" style={{ fontSize: 10 }}>
          No — excluded from KPI
        </Tag>
      </Flex>
      <DepositExclusions />
    </CCardBody>
  )
}
